import { basename, extname } from 'path';
import {
    getUploadContentType,
    IMAGE_JPEG_QUALITY,
    MAX_IMAGE_DIMENSION_PX,
    MAX_IMAGE_UPLOAD_SIZE_BYTES,
    getUploadFileValidationError,
} from './file-upload-rules.js';

export interface PreparedUploadBuffer {
    buffer: Buffer;
    filename: string;
    contentType: string;
    originalSize: number;
    storedSize: number;
    compressed: boolean;
}

export interface ImageCompressionResult {
    buffer: Buffer;
    filename: string;
    contentType: 'image/jpeg';
    compressed: boolean;
}

export type ImageCompressor = (buffer: Buffer, filename: string) => Promise<ImageCompressionResult>;

interface PrepareUploadOptions {
    compressor?: ImageCompressor;
}

function replaceExtension(filename: string, extension: string): string {
    const currentExt = extname(filename);
    if (!currentExt) return `${filename}${extension}`;
    return `${basename(filename, currentExt)}${extension}`;
}

async function hasOversizedImageDimension(buffer: Buffer): Promise<boolean> {
    try {
        const sharp = (await import('sharp')).default;
        const metadata = await sharp(buffer).metadata();
        return Math.max(metadata.width || 0, metadata.height || 0) > MAX_IMAGE_DIMENSION_PX;
    } catch {
        return false;
    }
}

export async function compressImageWithSharp(buffer: Buffer, filename: string): Promise<ImageCompressionResult> {
    const sharp = (await import('sharp')).default;
    const compressed = await sharp(buffer)
        .rotate()
        .resize({
            width: MAX_IMAGE_DIMENSION_PX,
            height: MAX_IMAGE_DIMENSION_PX,
            fit: 'inside',
            withoutEnlargement: true,
        })
        .jpeg({ quality: IMAGE_JPEG_QUALITY, mozjpeg: true })
        .toBuffer();

    return {
        buffer: compressed,
        filename: replaceExtension(filename, '.jpg'),
        contentType: 'image/jpeg',
        compressed: true,
    };
}

export async function prepareUploadBuffer(
    buffer: Buffer,
    filename: string,
    options: PrepareUploadOptions = {},
): Promise<PreparedUploadBuffer> {
    const contentType = getUploadContentType(filename);
    if (!contentType) {
        throw new Error('File không hợp lệ. Chỉ hỗ trợ HTML, Markdown, PNG, JPG, WebP, GIF.');
    }

    if (!contentType.startsWith('image/')) {
        const validation = getUploadFileValidationError(filename, buffer.length);
        if (validation) throw new Error(validation.message);
        return {
            buffer,
            filename,
            contentType,
            originalSize: buffer.length,
            storedSize: buffer.length,
            compressed: false,
        };
    }

    if (contentType === 'image/gif') {
        if (buffer.length > MAX_IMAGE_UPLOAD_SIZE_BYTES) {
            throw new Error('GIF quá lớn. Giới hạn: 2MB. Vui lòng giảm kích thước hoặc chuyển sang JPG/WebP trước khi upload.');
        }
        return {
            buffer,
            filename,
            contentType,
            originalSize: buffer.length,
            storedSize: buffer.length,
            compressed: false,
        };
    }

    const shouldCompress = buffer.length > MAX_IMAGE_UPLOAD_SIZE_BYTES || await hasOversizedImageDimension(buffer);
    if (!shouldCompress) {
        return {
            buffer,
            filename,
            contentType,
            originalSize: buffer.length,
            storedSize: buffer.length,
            compressed: false,
        };
    }

    const compressor = options.compressor || compressImageWithSharp;
    const compressed = await compressor(buffer, filename);
    if (compressed.buffer.length > MAX_IMAGE_UPLOAD_SIZE_BYTES) {
        throw new Error('Ảnh vẫn lớn hơn 2MB sau khi nén. Vui lòng giảm kích thước ảnh trước khi upload.');
    }

    return {
        buffer: compressed.buffer,
        filename: compressed.filename,
        contentType: compressed.contentType,
        originalSize: buffer.length,
        storedSize: compressed.buffer.length,
        compressed: compressed.compressed,
    };
}
